import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from '@rails/request.js'
import { Modal } from "bootstrap"
import { I18n } from 'i18n-js';
import locales from '../locales.json';
import { file_preview_status_path, file_preview_path } from "../routes"

// Connects to data-controller="file-group"
export default class extends Controller {
  static values = {
    createUrl: String,
    editTitleUrl: String,
    moveUrl: String,
    deleteUrl: String,
    fileId: String
  }

  connect() {
    this.i18n = new I18n(locales);
    this.i18n.locale = document.querySelector("meta[name='locale']").content
  }

  async updatePreview() {
    try {
      const response = await fetch(file_preview_path(this.fileIdValue))
      if (response.ok) {
        const html = await response.text()
        this.element.innerHTML = html
      }
    } catch (error) {
      console.error("Error updating preview:", error)
    }
  }

  async file_edit_dialog(event) {
    event.preventDefault();

    const result = await this._showInputModal({
      title: this.i18n.t('files.edit'),
      placeholder: this.i18n.t('files.file_title'),
      confirmText: this.i18n.t('helpers.save'),
      cancelText: this.i18n.t('helpers.actions.cancel'),
      denyText: this.i18n.t('files.delete'),
      required: false
    })

    if (result.confirmed && result.value) {
      const request = new FetchRequest(
        "post",
        this.editTitleUrlValue,
        {
          body: JSON.stringify({title: result.value, group_id: event.params.groupId}),
          responseKind: "turbo-stream"
        }
      )
      await request.perform()
    } else if (result.denied) {
      const request = new FetchRequest(
        "post",
        this.editTitleUrlValue,
        {
          body: JSON.stringify({title: "", group_id: event.params.groupId}),
          responseKind: "turbo-stream"
        }
      )
      await request.perform()
    }
  }

  async create_dialog(event) {
    event.preventDefault();

    const result = await this._showInputModal({
      title: this.i18n.t('files.create_group'),
      placeholder: this.i18n.t('files.group_name'),
      confirmText: this.i18n.t('helpers.save'),
      cancelText: this.i18n.t('helpers.actions.cancel'),
      required: true
    })

    if (result.confirmed && result.value) {
      const request = new FetchRequest(
        "post",
        this.createUrlValue,
        {
          body: JSON.stringify({name: result.value}),
          responseKind: "turbo-stream"
        }
      )
      await request.perform()
    }
  }

  async file_move_dialog(event) {
    event.preventDefault();

    const result = await this._showSelectModal({
      title: this.i18n.t('files.move'),
      options: event.params.groups,
      confirmText: this.i18n.t('helpers.save'),
      cancelText: this.i18n.t('helpers.actions.cancel'),
      required: true
    })

    if (result.confirmed && result.value) {
      const request = new FetchRequest(
        "post",
        this.moveUrlValue,
        {
          body: JSON.stringify({file_id: event.params.fileId, group_id: result.value, original_group_id: event.params.originalGroupId}),
          responseKind: "turbo-stream"
        }
      )
      await request.perform()
    }
  }

  async delete(event) {
    event.preventDefault();

    if (confirm(this.i18n.t('helpers.confirm'))) {
      const request = new FetchRequest(
        "delete",
        this.deleteUrlValue,
        {
          body: JSON.stringify({group_id: event.params.groupId}),
          responseKind: "turbo-stream"
        }
      )
      await request.perform()
    }
  }

  // --- Private: Bootstrap modal helpers ---

  _showInputModal({ title, placeholder, confirmText, cancelText, denyText, required }) {
    return new Promise((resolve) => {
      const id = `fg-modal-${Date.now()}`
      const hasDeny = !!denyText
      const requiredMsg = this.i18n.t('errors.messages.blank')

      const html = `
        <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-sm">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">${title}</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <input type="text" class="form-control" placeholder="${placeholder}" id="${id}-input" ${required ? 'required' : ''}>
                <div class="invalid-feedback">${requiredMsg}</div>
              </div>
              <div class="modal-footer">
                ${hasDeny ? `<button type="button" class="btn btn-danger me-auto" data-action="deny">${denyText}</button>` : ''}
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${cancelText}</button>
                <button type="button" class="btn btn-primary" data-action="confirm">${confirmText}</button>
              </div>
            </div>
          </div>
        </div>`

      document.body.insertAdjacentHTML('beforeend', html)
      const modalEl = document.getElementById(id)
      const input = document.getElementById(`${id}-input`)
      const modal = new Modal(modalEl)

      let resolved = false
      const cleanup = () => {
        if (resolved) return
        resolved = true
        modal.hide()
        modalEl.addEventListener('hidden.bs.modal', () => {
          modal.dispose()
          modalEl.remove()
        }, { once: true })
      }

      modalEl.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        if (required && !input.value.trim()) {
          input.classList.add('is-invalid')
          input.focus()
          return
        }
        const value = input.value.trim()
        resolved = true
        modal.hide()
        modalEl.addEventListener('hidden.bs.modal', () => {
          modal.dispose()
          modalEl.remove()
          resolve({ confirmed: true, denied: false, value })
        }, { once: true })
      })

      if (hasDeny) {
        modalEl.querySelector('[data-action="deny"]').addEventListener('click', () => {
          resolved = true
          modal.hide()
          modalEl.addEventListener('hidden.bs.modal', () => {
            modal.dispose()
            modalEl.remove()
            resolve({ confirmed: false, denied: true, value: '' })
          }, { once: true })
        })
      }

      modalEl.addEventListener('hidden.bs.modal', () => {
        if (!resolved) {
          resolved = true
          modal.dispose()
          modalEl.remove()
          resolve({ confirmed: false, denied: false, value: null })
        }
      })

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          modalEl.querySelector('[data-action="confirm"]').click()
        }
      })

      input.addEventListener('input', () => {
        input.classList.remove('is-invalid')
      })

      modal.show()
      modalEl.addEventListener('shown.bs.modal', () => input.focus(), { once: true })
    })
  }

  _showSelectModal({ title, options, confirmText, cancelText, required }) {
    return new Promise((resolve) => {
      const id = `fg-modal-${Date.now()}`
      const requiredMsg = this.i18n.t('errors.messages.blank')
      const selectPrompt = this.i18n.t('helpers.select.prompt')

      const optionsHtml = Object.entries(options)
        .map(([key, label]) => `<option value="${key}">${label}</option>`)
        .join('')

      const html = `
        <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered modal-sm">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">${title}</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <select class="form-select" id="${id}-select" ${required ? 'required' : ''}>
                  <option value="" selected disabled>${selectPrompt}</option>
                  ${optionsHtml}
                </select>
                <div class="invalid-feedback">${requiredMsg}</div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">${cancelText}</button>
                <button type="button" class="btn btn-primary" data-action="confirm">${confirmText}</button>
              </div>
            </div>
          </div>
        </div>`

      document.body.insertAdjacentHTML('beforeend', html)
      const modalEl = document.getElementById(id)
      const select = document.getElementById(`${id}-select`)
      const modal = new Modal(modalEl)

      let resolved = false

      modalEl.querySelector('[data-action="confirm"]').addEventListener('click', () => {
        if (required && !select.value) {
          select.classList.add('is-invalid')
          select.focus()
          return
        }
        const value = select.value
        resolved = true
        modal.hide()
        modalEl.addEventListener('hidden.bs.modal', () => {
          modal.dispose()
          modalEl.remove()
          resolve({ confirmed: true, value })
        }, { once: true })
      })

      modalEl.addEventListener('hidden.bs.modal', () => {
        if (!resolved) {
          resolved = true
          modal.dispose()
          modalEl.remove()
          resolve({ confirmed: false, value: null })
        }
      })

      select.addEventListener('change', () => {
        select.classList.remove('is-invalid')
      })

      modal.show()
      modalEl.addEventListener('shown.bs.modal', () => select.focus(), { once: true })
    })
  }
}
